import React from 'react';
import { useSelector } from 'react-redux';
import { clientNameSelector } from '../../../../redux/auth/selectors';
import GenericLineChart from '../../../charts/GenericLineChart';
import {
  HistoricalMrmData,
  generateMrmLineChartData,
} from './MarketRiskMeasurement';
import makeStyles from '@mui/styles/makeStyles';
import GridItem from '../../../layout/GridComponents/GridItem';
import {
  percentageToNdecialPlaces,
  percentageToTwoDecimalPlaces,
} from '../../../../utilities/numberFormatters';
import CustomTable from '../../../tables/CustomTable';
import { RaptorTheme, mainColors } from '../../../../styling/theme';
import clsx from 'clsx';
import {
  Area,
  AreaChart,
  Brush,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ExportButton from '../../../feedback/ExportButton';
import NoDataMessage from '../../../feedback/NoDataMessage.component';
import { Box, Typography } from '@mui/material';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { useMrmPageQuery } from '../../../../api/rs-hg/risk/hooks/queries';
import type { MrmPageModel, MrmPayload } from '../../../../api/rs-hg/risk/mrm.types';

interface HistoricalNavChartProps {
  payload: MrmPayload;
}

const historicalChartStyles = makeStyles<RaptorTheme>(() => ({
  toolbar: {
    padding: '0.62rem',
    display: 'flex',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: '1.56rem',
    color: mainColors.mainBlue,
    fontWeight: 300,
  },
  tooltip: {
    fontSize: '0.81rem',
    border: `1px solid ${mainColors.mainBlue}`,
  },
}));

const HistoricalChart: React.FC<HistoricalNavChartProps> = ({ payload }) => {
  const classes = historicalChartStyles();

  const srriNavData = payload.time_series_data_v2;

  const getMinMaxVol = (srriNavData: any[]) => {
    let minVol = 1;
    let maxVol = 0;

    srriNavData.forEach((date) => {
      if (date.srri_volatility) {
        if (date.srri_volatility > maxVol) {
          maxVol = date.srri_volatility;
        }
        if (date.srri_volatility < minVol) {
          minVol = date.srri_volatility;
        }
      }
    });

    return { minVol: minVol, maxVol: maxVol };
  };

  const getDynamicRiskClassAreas = (srriNavData: any[]) => {
    // Parse mrm_data from the data object to get actual risk class boundaries
    const mrmData = payload.mrm_data;

    // Convert percentage strings to decimal values and build risk class boundaries
    const parsePercentage = (str: string): number => {
      return Number(str.replace('%', '')) / 100;
    };

    const riskClassBoundaries: number[][] = [];
    Object.keys(mrmData)
      .sort((a, b) => Number(a) - Number(b))
      .forEach((key) => {
        const riskClassNum = Number(key);
        const limits = mrmData[key];

        const lowerBound = parsePercentage(limits[0]);
        // For risk class 7, upper bound is open-ended (use 1.0 as max)
        const upperBound = limits.length > 1 ? parsePercentage(limits[1]) : 1.0;

        riskClassBoundaries.push([riskClassNum, lowerBound, upperBound]);
      });

    const minMaxVol = getMinMaxVol(srriNavData);
    const areas: any[] = [];

    const riskClasseIndexesToInclude: number[] = [];
    let i = 0;
    riskClassBoundaries.forEach((bound) => {
      if (minMaxVol.maxVol >= bound[1] && minMaxVol.minVol <= bound[2]) {
        riskClasseIndexesToInclude.push(i);
      }
      i++;
    });

    riskClasseIndexesToInclude.forEach((i) => {
      if (
        riskClassBoundaries[i][1] < minMaxVol.minVol &&
        riskClassBoundaries[i][2] > minMaxVol.maxVol
      ) {
        areas.push(
          <ReferenceArea
            ifOverflow="extendDomain"
            y1={minMaxVol.minVol - 0.005}
            y2={minMaxVol.maxVol + 0.005}
            stroke={mainColors.NA}
            strokeOpacity={0.3}
            label={`Risk Class ${riskClassBoundaries[i][0]}`}
            fill={'transparent'}
          />,
        );
      } else if (riskClassBoundaries[i][1] < minMaxVol.minVol) {
        areas.push(
          <ReferenceArea
            ifOverflow="extendDomain"
            y1={minMaxVol.minVol - 0.005}
            y2={riskClassBoundaries[i][2]}
            stroke={mainColors.NA}
            strokeOpacity={0.3}
            label={`Risk Class ${riskClassBoundaries[i][0]}`}
            fill={'transparent'}
          />,
        );
      } else if (riskClassBoundaries[i][2] > minMaxVol.maxVol) {
        areas.push(
          <ReferenceArea
            ifOverflow="extendDomain"
            y1={riskClassBoundaries[i][1]}
            y2={minMaxVol.maxVol + 0.005}
            stroke={mainColors.NA}
            strokeOpacity={0.3}
            label={`Risk Class ${riskClassBoundaries[i][0]}`}
            fill={'transparent'}
          />,
        );
      } else {
        areas.push(
          <ReferenceArea
            ifOverflow="extendDomain"
            y1={riskClassBoundaries[i][1]}
            y2={riskClassBoundaries[i][2]}
            stroke={mainColors.NA}
            strokeOpacity={0.3}
            label={`Risk Class ${riskClassBoundaries[i][0]}`}
            fill={'transparent'}
          />,
        );
      }
    });

    return areas;
  };

  const buildCsvData = (srriNavData: any[]) => {
    const datesToKeep: any[] = [];
    srriNavData.forEach((date) => {
      if (date.srri_volatility) {
        datesToKeep.push({
          date: date.date,
          nav: Number(date.nav).toFixed(4),
          srri_volatility: date.srri_volatility,
          published_srri_risk_class: date.published_srri_risk_class,
          calculated_srri_risk_class: date.calculated_srri_risk_class,
        });
      }
    });
    return datesToKeep;
  };

  return (
    <GridItem
      xs={12}
      cardStyle={{ padding: '0.62rem', paddingLeft: '0.94rem' }}
      card
    >
      <Box className={classes.toolbar}>
        <Typography className={classes.title}>
          Historical Data ( NAV vs MRM )
        </Typography>
        <Box>
          <ExportButton
            exportData={buildCsvData(srriNavData)}
            pdfIdentifier={'id'}
            fields={[
              'date',
              'nav',
              'srri_volatility',
              'published_srri_risk_class',
              'calculated_srri_risk_class',
            ]}
            fileName={`MRM vs NAV`}
            allowPdfExport={false}
          />
        </Box>
      </Box>
      <ResponsiveContainer width={'100%'} height={400}>
        <LineChart
          data={srriNavData}
          syncId={'navVsSrri'}
          margin={{ left: 0, top: 30, bottom: 0, right: 50 }}
        >
          <XAxis
            dataKey={'date'}
            interval={'preserveStartEnd'}
            minTickGap={100}
          />
          <YAxis
            domain={[
              (dataMin: number) => dataMin - Math.abs(dataMin * 0.01),
              (dataMax: number) => dataMax + Math.abs(dataMax * 0.01),
            ]}
            interval={'preserveStartEnd'}
            scale="linear"
            allowDataOverflow={false}
            orientation={'left'}
          />
          <Tooltip
            wrapperClassName={classes.tooltip}
            formatter={(value, name) => {
              if (name === 'nav') {
                return [Number(value).toFixed(4), 'NAV'];
              } else {
                return [value, name];
              }
            }}
          />
          <Line
            type="monotone"
            dataKey={'nav'}
            stroke={mainColors.mainBlue}
            fill={mainColors.mainBlue}
            dot={{ stroke: 'transparent', strokeWidth: 2 }}
            activeDot={{ stroke: mainColors.mainBlue, strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
      <ResponsiveContainer width={'100%'} height={150}>
        <AreaChart data={srriNavData} syncId={'navVsSrri'}>
          <XAxis
            dataKey={'date'}
            interval={'preserveStartEnd'}
            minTickGap={100}
          />
          <YAxis
            domain={[
              (dataMin: number) => dataMin,
              (dataMax: number) => dataMax,
            ]}
            interval={'preserveStartEnd'}
            scale="linear"
            allowDataOverflow={false}
            orientation={'left'}
          />
          <Tooltip
            wrapperClassName={classes.tooltip}
            formatter={(value, name) => {
              if (name === 'srri_volatility') {
                return [Number(value).toFixed(4), 'SRRI'];
              } else {
                return [value, name];
              }
            }}
            active={false}
          />
          <Area
            connectNulls
            type="monotone"
            dataKey={'srri_volatility'}
            stroke={mainColors.mainBlue}
            fill={'transparent'}
            strokeWidth={2}
            dot={{ stroke: 'transparent', strokeWidth: 2 }}
            activeDot={{ stroke: mainColors.mainBlue, strokeWidth: 2 }}
          />

          {getDynamicRiskClassAreas(srriNavData)}
        </AreaChart>
      </ResponsiveContainer>
      <ResponsiveContainer width={'100%'} height={50}>
        <LineChart data={srriNavData} syncId={'navVsSrri'}>
          <Line
            type="monotone"
            dataKey="nav"
            stroke="transparent"
            dot={false}
            activeDot={false}
          />
          <Brush
            dataKey="date"
            height={15}
            stroke={mainColors.mainBlue_lightAlt}
            style={{ fontWeight: 100 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </GridItem>
  );
};

const statusBoxStyles = makeStyles<RaptorTheme>(() => ({
  statusBoxContainer: {
    padding: '1.25rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem',
  },
  title: {
    fontSize: '1.56rem',
    color: mainColors.mainBlue,
    fontWeight: 300,
  },
  levelsBox: {
    display: 'flex',
    gap: '1.25rem',
  },
  levelIndicator: {
    flexGrow: 1,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '0.62rem',
  },
  generalHeader: {
    height: '1.88rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '0.62rem 0.62rem 0 0',
    color: 'white',
    fontSize: '0.94rem',
    fontWeight: 500,
  },
  generalActiveHeader: {
    borderRight: `1px solid ${mainColors.NA_darker}`,
    borderTop: `1px solid ${mainColors.NA_darker}`,
    borderLeft: `1px solid ${mainColors.NA_darker}`,
  },
  passHeader: {
    backgroundColor: mainColors.Pass,
  },
  failExpectedHeader: {
    backgroundColor: mainColors.NA,
  },
  failActualHeader: {
    backgroundColor: mainColors.Fail,
  },
  indicatorBody: {
    padding: '1.25rem 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem',
    border: `1px solid ${mainColors.NA_darker}`,
  },
  levelLabel: {
    color: mainColors.mainBlue,
    fontWeight: 600,
  },
  boundariesLabel: {
    fontSize: '0.75rem',
    color: mainColors.mainBlue_lighter,
  },
}));

const MrmStatusBox: React.FC<{ payload: MrmPayload }> = ({ payload }) => {
  const classes = statusBoxStyles();

  // Helper function to parse percentage limit (e.g., "5%" -> 5)
  // Note: weeklyVolatility is already multiplied by 100 to convert from decimal to percentage
  const parseLimit = (limitString: string): number => {
    return Number(limitString.split('%')[0]);
  };

  const getIndicatorHeaderStyling = (
    key: string,
    itemLimits: string[],
    chosenLevel: string,
    weeklyVol: number,
  ) => {
    if (key === '7') {
      const lowerBound = parseLimit(itemLimits[0]);
      if (chosenLevel === key && weeklyVol >= lowerBound && weeklyVol < 100) {
        return clsx(
          classes.generalHeader,
          classes.generalActiveHeader,
          classes.passHeader,
        );
      } else if (chosenLevel === key && weeklyVol) {
        return clsx(
          classes.generalHeader,
          classes.generalActiveHeader,
          classes.failExpectedHeader,
        );
      } else if (weeklyVol >= lowerBound && weeklyVol < 100) {
        return clsx(
          classes.generalHeader,
          classes.generalActiveHeader,
          classes.failActualHeader,
        );
      } else {
        return classes.generalHeader;
      }
    } else {
      const lowerBound = parseLimit(itemLimits[0]);
      const upperBound = parseLimit(itemLimits[1]);
      if (
        chosenLevel === key &&
        weeklyVol >= lowerBound &&
        weeklyVol < upperBound
      ) {
        return clsx(
          classes.generalHeader,
          classes.generalActiveHeader,
          classes.passHeader,
        );
      } else if (chosenLevel === key && weeklyVol) {
        return clsx(
          classes.generalHeader,
          classes.generalActiveHeader,
          classes.failExpectedHeader,
        );
      } else if (weeklyVol >= lowerBound && weeklyVol < upperBound) {
        return clsx(
          classes.generalHeader,
          classes.generalActiveHeader,
          classes.failActualHeader,
        );
      } else {
        return classes.generalHeader;
      }
    }
  };

  const getIndicatorHeaderLabel = (
    key: string,
    itemLimits: string[],
    chosenLevel: string,
    weeklyVol: number,
  ) => {
    // Logger: Log inputs for each risk class evaluation
    // Note: weeklyVol is already multiplied by 100 to match bounds scale
    const lowerBound = parseLimit(itemLimits[0]);
    const upperBound = key === '7' ? 100 : parseLimit(itemLimits[1]);

    if (key === '7') {
      if (chosenLevel === key && weeklyVol >= lowerBound && weeklyVol < 100) {
        return 'PASS';
      } else if (chosenLevel === key && weeklyVol) {
        return 'PUBLISHED';
      } else if (weeklyVol >= lowerBound && weeklyVol < 100) {
        return 'ACTUAL';
      } else {
        return '';
      }
    } else {
      if (
        chosenLevel === key &&
        weeklyVol >= lowerBound &&
        weeklyVol < upperBound
      ) {
        return 'PASS';
      } else if (chosenLevel === key && weeklyVol) {
        return 'PUBLISHED';
      } else if (weeklyVol >= lowerBound && weeklyVol < upperBound) {
        return 'ACTUAL';
      } else {
        return '';
      }
    }
  };

  return (
    <GridItem xs={12} card className={classes.statusBoxContainer}>
      <Typography className={classes.title}>MRM Status</Typography>
      <Box className={classes.levelsBox}>
        {Object.keys(payload.mrm_data).map((key) => {
          const riskClassData = payload.mrm_data;
          const weeklyVol = payload.weekly_volatility * 100;
          const publishedLevel = String(payload.limits[2]);

          return (
            <Box key={key} className={classes.levelIndicator}>
              <Box
                className={getIndicatorHeaderStyling(
                  key,
                  riskClassData[key],
                  publishedLevel,
                  weeklyVol,
                )}
              >
                {getIndicatorHeaderLabel(
                  key,
                  riskClassData[key],
                  publishedLevel,
                  weeklyVol,
                )}
              </Box>
              <Box className={classes.indicatorBody}>
                <Typography className={classes.levelLabel}>
                  Risk Class {key}
                </Typography>
                <Typography className={classes.boundariesLabel}>
                  {riskClassData[key].length > 1
                    ? `${riskClassData[key][0]} <= MRM < ${riskClassData[key][1]}`
                    : `${riskClassData[key][0]} <= MRM`}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </GridItem>
  );
};

const formatName = (el: string) => {
  const spaced = el.replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced.includes(' ')
    ? spaced.charAt(0).toUpperCase() + spaced.slice(1)
    : el.toUpperCase();
};

const HistoricalGraphics: React.FC<{ payload: MrmPayload }> = ({ payload }) => {
  const chartData = generateMrmLineChartData(payload);
  const tableData = [...chartData];
  // Get the name user id
  const clientName = useSelector(clientNameSelector);
  // Create the formatter for the srri value based on the user id
  const formatter = clientName?.includes('coutts')
    ? (num: number | string) => percentageToNdecialPlaces(num, 9)
    : (num: number | string) => percentageToNdecialPlaces(num, 4);

  tableData.reverse();

  return (
    <>
      <GridItem
        xs={8}
        card
        style={{ alignSelf: 'stretch' }}
        cardStyle={{ padding: '1.25rem', height: '100%' }}
      >
        <GenericLineChart
          title="Historical MRM - Chart"
          margin={{ top: 20, left: 0, right: 40, bottom: 20 }}
          error={null}
          yAxisDomain={[
            (dataMin: number) => dataMin - Math.abs(dataMin * 0.1),
            (dataMax: number) => dataMax + Math.abs(dataMax * 0.05),
          ]}
          showTitle
          width="100%"
          height={600}
          data={chartData}
          lines={[
            { dataKey: 'upperBound', color: mainColors.Fail },
            { dataKey: 'lowerBound', color: mainColors.Alert },
            { dataKey: 'srri', color: mainColors.mainBlue },
          ]}
          tooltipFormatter={(value: number, name: string) => {
            return [percentageToTwoDecimalPlaces(value), formatName(name)];
          }}
          xAxisDataKey="date"
          yAxisTickFormatter={(value: number) =>
            percentageToTwoDecimalPlaces(value)
          }
        />
      </GridItem>
      <GridItem card xs={4}>
        <CustomTable<HistoricalMrmData>
          data={tableData}
          columns={['date', 'lowerBound', 'srri', 'upperBound'].map((el) => ({
            field: el,
            title: formatName(el),
            headerStyle: {
              textAlign: 'center',
            },
            cellStyle: {
              textAlign: 'center',
            },
            render: (rowData: HistoricalMrmData) =>
              el !== 'date'
                ? formatter(rowData[el as keyof HistoricalMrmData])
                : rowData['date'],
          }))}
          options={{ paging: true, pageSize: 10, exportButton: true }}
          showToolbar
          title="Historical MRM - Table"
          toolbarComponents={{
            titleStyle: {
              fontWeight: 300,
            },
          }}
        />
      </GridItem>
    </>
  );
};

export const MarketRiskMeasurementComponents: React.FC<{
  payload: MrmPayload;
}> = ({ payload }) => {
  return payload && typeof payload === 'object' && !Array.isArray(payload) ? (
    <>
      <HistoricalChart payload={payload} />
      <MrmStatusBox payload={payload} />
      <HistoricalGraphics payload={payload} />
    </>
  ) : (
    <NoDataMessage message={'No Data Available For This Share Class'} />
  );
};

const MarketRiskMeasurement = (props: FundInfoComponentProps) => {
  const mrmQuery = useMrmPageQuery(props.fundId);

  return (
    <QueryBoundary<MrmPageModel>
      isLoading={mrmQuery.isLoading}
      isFetching={mrmQuery.isFetching}
      isError={mrmQuery.isError}
      error={mrmQuery.error}
      data={
        mrmQuery.data?.data &&
        typeof mrmQuery.data.data === 'object' &&
        !Array.isArray(mrmQuery.data.data)
          ? mrmQuery.data
          : null
      }
      loadingMessages={['Loading MRM Data', 'Just a few more seconds...']}
      errorMessage="No MRM Data Available."
      noDataMessage="No MRM Data Available."
    >
      {(pageModel) => (
        <MarketRiskMeasurementComponents payload={pageModel.data} />
      )}
    </QueryBoundary>
  );
};
export default MarketRiskMeasurement;
